import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbActiveModal, NgbTypeahead } from '@ng-bootstrap/ng-bootstrap';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { combineLatest, Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, switchMap, catchError, map, startWith } from 'rxjs/operators';
import { Folder, FolderPlanogram } from 'src/app/models/folder';
import { StoreSearchEntry } from 'src/app/models/store-search-entry';
import { ApiService } from 'src/app/services/api.service';
import { FolderApiService } from 'src/app/services/folder-api.service';
import { PlanogramApiService } from 'src/app/services/planogram-api.service';
import { PlanogramService } from 'src/app/services/planogram.service';
import { ToastService } from 'src/app/services/toasts.service';
import { ClonePlanogramRequest } from 'src/app/requests';
import { Merchandise } from 'src/app/models/merchandise';

@Component({
    selector: 'app-clone-planogram-modal',
    templateUrl: './clone-planogram-modal.component.html',
    styleUrl: './clone-planogram-modal.component.scss',
    imports: [FormsModule, ReactiveFormsModule, NgbTypeahead]
})
export class ClonePlanogramModalComponent implements OnInit {
  @Input() planogram!: FolderPlanogram;
  @Input() planogramFolderId!: string;
  @Input() rootFolders: Folder[] = [];

  cloneForm: FormGroup;
  flatFolders: { id: string; name: string }[] = [];
  isSubmitting = false;

  private destroyRef = inject(DestroyRef);

  constructor(
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private apiService: ApiService,
    private folderApiService: FolderApiService,
    private planogramService: PlanogramService,
    private toastService: ToastService,
    private planogramApiService: PlanogramApiService
  ) {
    this.cloneForm = this.fb.group({
      store: [null, Validators.required],
      merch: [null],
      folderId: [''],
      name: [''],
      autoName: [false]
    });
  }

  ngOnInit(): void {
    this.flatFolders = this.flattenFolders(this.rootFolders);

    // Watch store, merch, and autoName checkbox to manage UI state and name generation
    combineLatest([
      this.cloneForm.get('store')!.valueChanges.pipe(startWith(this.cloneForm.get('store')?.value)),
      this.cloneForm.get('merch')!.valueChanges.pipe(startWith(this.cloneForm.get('merch')?.value)),
      this.cloneForm.get('autoName')!.valueChanges.pipe(startWith(false))
    ]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(([store, merch, autoName]) => {
      // In Typeahead, value is an object once selected, or string while typing
      const hasStoreSelected = store && typeof store !== 'string';
      const hasMerchSelected = merch && typeof merch !== 'string';

      // 1. Manage Disabled States via Form Controls
      if (hasStoreSelected) {
        this.cloneForm.get('merch')?.enable({ emitEvent: false });
      } else {
        this.cloneForm.get('merch')?.disable({ emitEvent: false });
        this.cloneForm.get('merch')?.setValue(null, { emitEvent: false });
      }

      if (hasStoreSelected && hasMerchSelected) {
        this.cloneForm.get('autoName')?.enable({ emitEvent: false });
      } else {
        this.cloneForm.get('autoName')?.disable({ emitEvent: false });
        // If it was checked but dependencies are now gone, uncheck it
        if (autoName) {
          this.cloneForm.get('autoName')?.setValue(false, { emitEvent: false });
          autoName = false;
        }
      }

      // 2. Handle Auto-Naming Logic
      if (autoName && hasStoreSelected && hasMerchSelected) {
        const storeName = store.name || '';
        const merchName = merch.name || '';
        const drops = this.planogram.drops;
        const generatedName = `${storeName} - ${merchName} (${drops} Drops)`;
        this.cloneForm.get('name')?.patchValue(generatedName, { emitEvent: false });
      } else if (this.cloneForm.get('autoName')?.dirty && !autoName) {
        // If user manually unchecked the box, clear the name field
        this.cloneForm.get('name')?.patchValue('', { emitEvent: false });
      }
    });

    // Default to a folder if logic dictates, otherwise default folder is handled by empty string
    this.cloneForm.patchValue({
      folderId: this.planogramFolderId 
    });
  }

  get latestState() {
    if (!this.planogram.states || this.planogram.states.length === 0) return null;
    return [...this.planogram.states].sort((a, b) => 
      new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime()
    )[0];
  }

  private flattenFolders(folders: Folder[], level = 0): { id: string; name: string }[] {
    let flatList: { id: string; name: string }[] = [];
    for (const folder of folders) {
      flatList.push({
        id: folder.guid,
        name: (level > 0 ? '\u00A0\u00A0'.repeat(level) + '↳ ' : '') + folder.name
      });
      if (folder.subFolders) {
        flatList = flatList.concat(this.flattenFolders(folder.subFolders, level + 1));
      }
    }
    return flatList;
  }

  searchStores = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      filter(term => term.length >= 2),
      switchMap(term => this.apiService.searchStores(term, this.planogram.dataSourceId).pipe(catchError(() => of([]))))
    );

  storeFormatter = (store: StoreSearchEntry) => store.name;

  searchMerch = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      filter(term => term.length >= 2),
      switchMap(term => {
        const store = this.cloneForm.get('store')?.value as StoreSearchEntry;
        if (!store) return of([]);
        return this.apiService.getMerchandiseCategories(term, store.storeGuid).pipe(catchError(() => of([])));
      })
    );

  merchFormatter = (merch: Merchandise) => merch.name;

  async submit() {
    if (this.cloneForm.invalid) return;

    const val = this.cloneForm.value;
    const request : ClonePlanogramRequest = {
      planogramId: this.planogram.guid,
      stateId: this.planogram.states.sort((a,b) => new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime())[0].guid,
      folderId: val.folderId || null,
      storeId: val.store.storeGuid,
      merchId: val.merch?.id || null,
      name: val.name || null
    };

    try {
      const planogram = await this.planogramApiService.clonePlanogram(request);
      this.toastService.show('Planogram cloned successfully!', {
        classname: 'bg-success text-light',
        delay: 3000,
      });
      this.activeModal.close(planogram);
    } 
    catch (err) {
      console.error(err);
      this.toastService.show('Failed to clone planogram.', {
        classname: 'bg-danger text-light',
        delay: 4000,
      });
    } 
    finally {
      this.isSubmitting = false;
    }
  }
}